import { z } from 'zod';

// Loose schemas: every field is optional because the admin form sends
// a *patch* (partial update). We still want bounds-checking on individual
// fields so a typo can't push absurd values into runtime config that the
// mobile apps consume.

const dollars = z.number().nonnegative().max(100000);
const ratio = z.number().min(0).max(1);
const positiveInt = z.number().int().positive();
const nonNegInt = z.number().int().nonnegative();

export const PricingConfigSchema = z
  .object({
    basePayFlat: dollars,
    payPerMile: dollars,
    payPerMinute: dollars,
    minimumPay: dollars,
    driverPercentage: ratio,
    platformFeeRate: ratio,
    tipDriverPercentage: ratio,
    surgeMultiplier: z.number().min(1).max(10),
  })
  .partial()
  .passthrough()
  .refine(
    (v) => {
      if (v.driverPercentage == null && v.platformFeeRate == null) return true;
      const a = v.driverPercentage ?? 0;
      const b = v.platformFeeRate ?? 0;
      return Math.abs(a + b - 1) < 0.001;
    },
    {
      message: 'driverPercentage + platformFeeRate must sum to 1.00',
      path: ['driverPercentage'],
    }
  );

export const ServiceAreaSchema = z
  .object({
    maxAssignDistanceMiles: z.number().positive().max(500),
    placesAutocompleteRadiusMeters: z.number().positive().max(200000),
    excludedZipPrefixes: z.array(z.string().regex(/^\d{1,5}$/)).max(500),
    includedZipPrefixes: z.array(z.string().regex(/^\d{1,5}$/)).max(500),
  })
  .partial()
  .passthrough();

export const OperationsSchema = z
  .object({
    jobOfferWindowSeconds: positiveInt.max(600),
    notificationTtlHours: positiveInt.max(168),
    scheduledReminderMinutes: z.array(nonNegInt).max(20),
    missedPickupGraceMinutes: nonNegInt.max(180),
    geofenceRadiusMeters: positiveInt.max(5000),
    driverLocationIntervalSeconds: positiveInt.max(300),
    driverLocationDistanceFilterMeters: nonNegInt.max(2000),
    driverHeartbeatSeconds: positiveInt.max(600),
  })
  .partial()
  .passthrough();

export const FeatureFlagsSchema = z
  .record(z.string().min(1), z.boolean())
  .refine((obj) => Object.keys(obj).every((k) => /^[a-zA-Z_][\w]{0,63}$/.test(k)), {
    message: 'Flag keys must be alphanumeric/underscore (max 64 chars).',
  });

const CatalogMap = z.record(
  z.string().min(1).max(64),
  z.number().nonnegative().max(100000)
);

export const CatalogsVehiclesSchema = z
  .object({ multipliers: CatalogMap })
  .partial()
  .passthrough();

export const CatalogsEquipmentSchema = z
  .object({ prices: CatalogMap })
  .partial()
  .passthrough();

const SCHEMAS: Record<string, z.ZodTypeAny> = {
  pricing: PricingConfigSchema,
  service_area: ServiceAreaSchema,
  operations: OperationsSchema,
  feature_flags: FeatureFlagsSchema,
  catalogs_vehicles: CatalogsVehiclesSchema,
  catalogs_equipment: CatalogsEquipmentSchema,
};

export class ConfigValidationError extends Error {
  readonly issues: z.ZodIssue[];
  constructor(message: string, issues: z.ZodIssue[]) {
    super(message);
    this.name = 'ConfigValidationError';
    this.issues = issues;
  }
}

/**
 * Validate a config patch against the schema registered for `docId`.
 * Throws ConfigValidationError on failure. Returns the (possibly coerced)
 * patch on success. Unknown docIds pass through unchanged.
 */
export function validateConfigPatch(
  docId: string,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const schema = SCHEMAS[docId];
  if (!schema) return patch;
  const result = schema.safeParse(patch);
  if (!result.success) {
    const summary = result.error.issues
      .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('; ');
    throw new ConfigValidationError(
      `Invalid ${docId} config: ${summary}`,
      result.error.issues
    );
  }
  return result.data as Record<string, unknown>;
}
